import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLabels, usePrefs, useRequestData } from 'wsm-common-data';
import { removeAllFilters } from 'wsm-srp-utilities';
import { trackEvent } from 'ddc-track-event';
import NestedFacetList from './NestedFacetList';
import NestedFacetPanel from './NestedFacetPanel';
import {
	NESTED_PANEL_OPEN,
	NESTED_PANEL_CLOSED,
	CLOSE_FILTERS_MODAL,
	safePublish
} from './pubsub-events';
import { clearFacetGroupSelections } from '../../utilities/clear-filters';

// Transition duration in ms. Used only for the fallback timeout in focus
// management. The actual CSS duration comes from Kinetic utility classes
// `transition-duration-md` (200ms, mobile) or `transition-duration-lg`
// (300ms, desktop), and reduced-motion is handled by Kinetic's global
// @media (prefers-reduced-motion: reduce) rule.
const TRANSITION_DURATION = 300;

// Helper: multi-group items carry groupId directly (matching the props
// accordion passes to MultiFacetGroup); single-facet items use facet.id.
const getFacetGroupId = (fg) => fg.groupId || fg.facet.id;

const NestedFacetNavigation = ({ facetGroups }) => {
	const [activeFacetId, setActiveFacetId] = useState(null);
	const [isTransitioning, setIsTransitioning] = useState(false);
	// Defer aria-live content until first transition to avoid initial-render announcement
	const [announcement, setAnnouncement] = useState('');
	const containerRef = useRef(null);
	const backLinkRef = useRef(null);
	const headingRef = useRef(null);
	const panelHeadingRef = useRef(null);
	const activeFacetTitleRef = useRef(null);
	const listContainerRef = useRef(null);
	const panelContainerRef = useRef(null);
	const previousActiveFacetIdRef = useRef(null);
	const labels = useLabels();
	const prefs = usePrefs();
	const { deviceType, widgetName, windowId } = useRequestData();
	const isDesktop = deviceType === 'DESKTOP';
	const stickyActive = (prefs?.enableStickyScroll === true || prefs?.enableStickyScroll === 'true') 
							&& (prefs?.facetDisplay === 'nested') && isDesktop;
	const durationClass = isDesktop
		? 'transition-duration-lg'
		: 'transition-duration-md';

	// Derive the active facet group from fresh props on every render,
	// so filter selections are never stale (mirrors how the accordion
	// path always passes fresh facet data to FacetGroup).
	const activeFacet = activeFacetId
		? facetGroups.find((fg) => getFacetGroupId(fg) === activeFacetId) ||
		  null
		: null;

	const allFiltersLabel = labels.get('ALL_FILTERS');
	const resetLabel = labels.get('RESET');
	// appliedFilters is the canonical signal for "user has applied something".
	// WIS only includes filters with non-empty values, so length > 0 is exactly
	// the question we want — and it sidesteps the geoLocation/geoRadius id
	// mismatch in buildFacetGroupData. Same pattern ws-inv-filters uses for
	// Clear Filters. Per Kathryn's PR 178 review (TA4005987).
	const hasAppliedFilters =
		(window.DDC?.InvData?.getAppliedFilters?.() ?? []).length > 0;

	const handleSelectFacet = useCallback(
		(facetGroup) => {
			const id = getFacetGroupId(facetGroup);
			activeFacetTitleRef.current = id;
			setIsTransitioning(true);
			setActiveFacetId(id);
			// Update announcement on transition, not on initial render
			setAnnouncement(
				`${
					facetGroup.groupTitleLabel
						? labels.get(facetGroup.groupTitleLabel)
						: facetGroup.facet.label
				} ${labels.get('FILTERS')}`
			);
		},
		[labels]
	);

	const handleBackToList = useCallback(() => {
		setIsTransitioning(true);
		setActiveFacetId(null);
		setAnnouncement(allFiltersLabel);
	}, [allFiltersLabel]);

	// No local SR announcement on reset — ws-inv-filters announces the
	// vehicle-count change after the inventory re-fetch, which is the
	// meaningful "something happened" cue for SR users. A local
	// announcement here would either name the current panel ("All
	// Filters") or echo a bare facet name without a verb — both add
	// noise without adding information.
	const handleResetAll = useCallback(() => {
		removeAllFilters(resetLabel, widgetName, windowId);
		if (headingRef.current) {
			headingRef.current.focus();
		}
	}, [resetLabel, widgetName, windowId]);

	const handleResetFacet = useCallback(() => {
		if (!activeFacet) {
			return;
		}
		trackEvent(widgetName, windowId, {
			element: 'Clear nested facet',
			elementCTA: resetLabel,
			result: 'Results updated with selections from a nested facet removed'
		});
		clearFacetGroupSelections(activeFacet);
		// Focus the panel's own heading rather than the back link — Reset
		// clears the active facet's selections but the user stays on the
		// same panel. Focusing the back link would imply navigation away.
		// The Reset button itself disappears when hasSelections flips
		// false, so leaving focus untouched would fall back to
		// document.body and lose orientation.
		if (panelHeadingRef.current) {
			panelHeadingRef.current.focus();
		}
	}, [activeFacet, resetLabel, widgetName, windowId]);

	const handleCloseModal = useCallback(() => {
		// Use the control event, not the lifecycle notification. The
		// parent runs hideModal → animation → setModalShow(false), then
		// fires modal-hide post-state, which unmounts the portal contents.
		// The unmount-cleanup effect below fires nested-panel-closed at
		// that point — keeping it out of this handler avoids a brief
		// flash of sister chrome (heading + close X + chips) above the
		// nested panel during the 300ms exit animation.
		safePublish(CLOSE_FILTERS_MODAL);
	}, []);

	// Publish nested-panel-open / nested-panel-closed on view transitions.
	// Idempotent: only publishes when activeFacetId actually changes.
	useEffect(() => {
		const previous = previousActiveFacetIdRef.current;
		if (previous === activeFacetId) {
			return;
		}
		if (activeFacetId) {
			safePublish(NESTED_PANEL_OPEN, { facetId: activeFacetId });
		} else if (previous) {
			safePublish(NESTED_PANEL_CLOSED);
		}
		previousActiveFacetIdRef.current = activeFacetId;
	}, [activeFacetId]);

	// Unmount cleanup: if we were in a nested panel when the component
	// tears down (portal unmount on modal close, page navigation, etc.),
	// publish nested-panel-closed so the sister widget's nestedPanelOpen
	// resets to false. Without this, modal-close paths that bypass
	// handleCloseModal — the footer "Show X Vehicles" button (VehicleCount
	// calls setModalShow(false) directly), or any external
	// ws-inv-filters/close-filters trigger — leave nestedPanelOpen=true,
	// and the sister's chrome (heading + close X + chips) stays
	// suppressed on the next modal open.
	useEffect(
		() => () => {
			if (previousActiveFacetIdRef.current) {
				safePublish(NESTED_PANEL_CLOSED);
			}
		},
		[]
	);

	// Scope Escape handler to containerRef instead of document
	useEffect(() => {
		if (!activeFacet) {
			return undefined;
		}
		const container = containerRef.current;
		if (!container) {
			return undefined;
		}
		const handleKeyDown = (event) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				event.stopPropagation();
				handleBackToList();
			}
		};
		container.addEventListener('keydown', handleKeyDown);
		return () => container.removeEventListener('keydown', handleKeyDown);
	}, [activeFacet, handleBackToList]);

	// Focus management using transitionend event with fallback timeout
	useEffect(() => {
		if (!isTransitioning) {
			return undefined;
		}

		// Determine which container is transitioning into view
		const transitionTarget = activeFacet
			? panelContainerRef.current
			: listContainerRef.current;

		let settled = false;

		const moveFocus = () => {
			if (settled) {
				return;
			}
			settled = true;
			setIsTransitioning(false);

			if (activeFacet && backLinkRef.current) {
				backLinkRef.current.focus();
			} else if (
				!activeFacet &&
				activeFacetTitleRef.current &&
				containerRef.current
			) {
				const facetButton = containerRef.current.querySelector(
					`[data-facet-id="${activeFacetTitleRef.current}"]`
				);
				if (facetButton) {
					facetButton.focus();
				}
			}
		};

		// Listen for transitionend on the container sliding into view
		if (transitionTarget) {
			transitionTarget.addEventListener('transitionend', moveFocus, {
				once: true
			});
		}

		// Fallback timeout in case transitionend doesn't fire
		// (e.g., element removed mid-transition, display:none applied)
		const fallbackTimer = setTimeout(moveFocus, TRANSITION_DURATION + 50);

		return () => {
			settled = true;
			if (transitionTarget) {
				transitionTarget.removeEventListener(
					'transitionend',
					moveFocus
				);
			}
			clearTimeout(fallbackTimer);
		};
	}, [activeFacet, isTransitioning]);

	const showList = !activeFacet;
	const showPanel = !!activeFacet;

	// Kinetic utility classes for each panel state
	const activeClasses =
		'translate-0 opacity-100 transition-timing-function-decelerate';
	const offscreenLeftClasses =
		'absolute-top absolute-left w-100 translate-left-100 opacity-0 pointer-events-none transition-timing-function-sharp';
	const offscreenRightClasses =
		'absolute-top absolute-left w-100 translate-right-100 opacity-0 pointer-events-none transition-timing-function-sharp';

	return (
		<div
			className={`nested-facet-navigation${
				stickyActive ? ' facet-list-sticky' : ''
			}`}
			ref={containerRef}
			role="group"
			aria-label={allFiltersLabel}
		>
			{/* Empty on initial render, populated after first transition */}
			<div aria-live="polite" aria-atomic="true" className="sr-only">
				{announcement}
			</div>
			<div className="nested-facet-panels position-relative">
				<div
					ref={listContainerRef}
					className={`nested-facet-container ${durationClass} ${
						showList ? activeClasses : offscreenLeftClasses
					}`}
					aria-hidden={!showList}
				>
					{showList && (
						<NestedFacetList
							facetGroups={facetGroups}
							onSelectFacet={handleSelectFacet}
							hasAppliedFilters={hasAppliedFilters}
							onResetAll={handleResetAll}
							headingRef={headingRef}
						/>
					)}
				</div>
				<div
					ref={panelContainerRef}
					className={`nested-facet-container ${durationClass} ${
						showPanel ? activeClasses : offscreenRightClasses
					}`}
					aria-hidden={!showPanel}
				>
					{showPanel && (
						<NestedFacetPanel
							facetGroup={activeFacet}
							allFiltersLabel={allFiltersLabel}
							onBack={handleBackToList}
							backLinkRef={backLinkRef}
							panelHeadingRef={panelHeadingRef}
							onResetFacet={handleResetFacet}
							onCloseModal={handleCloseModal}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

NestedFacetNavigation.propTypes = {
	facetGroups: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};

export default NestedFacetNavigation;
